import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { setModal } from 'slices/modalSlice'
import { setFiltrosSeleccionados, setFiltroLocal } from '../../../slices/filterSlice'
import { Buscador } from '../buscador/Buscador'
import { FechaSelect } from '../fecha/FechaSelect'
import { GenericModal } from '../modal/GenericModal'
import styles from './Filtros.module.css'
import { RadioInput } from './RadioInput'
import { useFiltros } from './useFiltros'

const MAX_OPCIONES_VISIBLES = 5

export const Filtros = ({
  mapaFiltros,
  contextoFiltros,
  filtrosVisibles,
  arrayAFiltrar,
  buscador
}) => {
  const dispatch = useDispatch()
  const { categoriasPath, filtrosCaracteristicas, filtrosLocales } = useSelector(
    (state) => state.filters
  )

  const filtrosSeleccionados = useSelector(
    (state) => state.filters.filtrosSeleccionados,
    shallowEqual
  )
  const modalMetadata = useSelector((state) => state.genericModal.metadata)

  const {
    categorias,
    vendedor,
    setVendedor,
    onChangeCategoria,
    categoriaAnterior,
    onResetCategorias,
    limpiarTodosFiltros,
    handleFechaChange,
    cargandoFiltros,
    cargandoCategorias
  } = useFiltros(arrayAFiltrar)

  const filtrosFijos = useMemo(() => {
    if (!mapaFiltros) {
      return []
    }
    return Object.entries(mapaFiltros)
      .filter(([campo, config]) => {
        if (!config.opciones) {
          return false
        }
        if (filtrosVisibles?.length) {
          return filtrosVisibles.includes(campo)
        }
        return true
      })
      .map(([campo, config]) => {
        const key = typeof config.key === 'function' ? config.key(contextoFiltros) : campo
        return {
          titulo:
            typeof config.titulo === 'function' ? config.titulo(contextoFiltros) : config.titulo,
          value: filtrosLocales[key],
          opciones: config.opciones(contextoFiltros),
          onChange: (v) => dispatch(setFiltroLocal({ key, value: v }))
        }
      })
  }, [mapaFiltros, contextoFiltros, filtrosLocales, filtrosVisibles, dispatch])

  const verTodas = (opciones, tipo = 'categoria', etiqueta = null) => {
    dispatch(
      setModal({
        size: 'lg',
        title:
          tipo === 'categoria' ? 'Selecciona una categoría' : `Filtrar por ${etiqueta || tipo}`,
        type: '',
        show: true,
        metadata: { tipo, etiqueta }
      })
    )
    dispatch(setModal({ items: opciones }))
  }

  const ordenarOpciones = (opciones, value) => {
    if (value === null || value === '') {
      return opciones
    }

    return [...opciones].sort((a, b) => {
      if (String(a.valor) === String(value)) {
        return -1
      }
      if (String(b.valor) === String(value)) {
        return 1
      }
      return 0
    })
  }

  const handlePrecioChange = (value, tipo) => {
    if (value === '' || Number(value) >= 0) {
      dispatch(setFiltroLocal({ key: tipo === 'desde' ? 'precioDesde' : 'precioHasta', value }))
    }
  }

  const handleKeyDownPrecio = (e) => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault()
    }
  }

  const entradasFecha = useMemo(
    () =>
      mapaFiltros
        ? Object.entries(mapaFiltros)
            .filter(([, c]) => c.tipo === 'fecha')
            .map(([key, c]) => ({ key, label: c.label }))
        : [],
    [mapaFiltros]
  )

  const mostrarPrecio = useMemo(
    () =>
      mapaFiltros && filtrosVisibles.some((f) => f === 'precio')
        ? Object.values(mapaFiltros).some((c) => c.tipo === 'precio')
        : false,
    [mapaFiltros, filtrosVisibles]
  )

  const muestraLimpiaFiltros =
    categoriasPath.length > 0 ||
    Object.values(filtrosSeleccionados).some((v) => v) ||
    (buscador && buscador.valor) ||
    filtrosLocales.precioDesde ||
    filtrosLocales.precioHasta ||
    entradasFecha.some((e) => filtrosLocales[e.key]) ||
    filtrosFijos.some((f) => f.value)

  const seleccionarOpcionModal = (item) => {
    const tipo = modalMetadata?.tipo || 'categoria'
    if (tipo === 'filtroFijo') {
      const filtroSeleccionado = filtrosFijos.find(
        (filtro) => filtro.titulo === modalMetadata?.etiqueta
      )
      if (filtroSeleccionado?.onChange) {
        filtroSeleccionado.onChange(item.valor)
      }
    } else if (tipo === 'categoria') {
      onChangeCategoria(item.value, item.label)
    } else if (tipo === 'caracteristica') {
      const etiqueta = modalMetadata?.etiqueta
      if (etiqueta) {
        dispatch(setFiltrosSeleccionados({ ...filtrosSeleccionados, [etiqueta]: item.value }))
      }
    }
  }

  const limpiarFiltros = () => {
    limpiarTodosFiltros()

    if (buscador?.setBusqueda) {
      buscador.setBusqueda('')
    }
  }

  return (
    <div className={`${styles.filtros}`}>
      {cargandoFiltros || cargandoCategorias ? (
        <div className="d-flex justify-content-center p-4">
          <Spinner animation="border" role="status" aria-label="Cargando" />
        </div>
      ) : (
        <>
          <GenericModal onSelect={seleccionarOpcionModal} />
          {buscador && (
            <Buscador
              onChange={buscador.onChange}
              placeholder={buscador.placeholder}
              busqueda={buscador.valor}
              setBusqueda={buscador.setBusqueda}
            />
          )}
          {muestraLimpiaFiltros && (
            <div className={styles.limpiarFiltrosContainer}>
              <button className={styles.btnLimpiarFiltros} onClick={limpiarFiltros}>
                Limpiar filtros
              </button>
            </div>
          )}
          {(categoriasPath.length > 0 || (vendedor && vendedor.id !== 0)) && (
            <div className={styles.filtrosActivos}>
              {categoriasPath.length > 0 && (
                <div className={styles.pathContainer}>
                  {categoriasPath.map((cat, index) => (
                    <div key={cat.id}>
                      <span
                        className={styles.categoriaPath}
                        onClick={() => categoriaAnterior(cat.id)}
                      >
                        {cat.label}
                      </span>
                      {index < categoriasPath.length - 1 && ' > '}
                    </div>
                  ))}
                  <span className={styles.clearPath} onClick={onResetCategorias}>
                    <i className="bi bi-x"></i>
                  </span>
                </div>
              )}
              {vendedor && vendedor.id !== 0 && (
                <div className={styles.pathContainer}>
                  <span className={styles.categoriaPath}>{vendedor.label}</span>
                  <span
                    className={styles.clearPath}
                    onClick={() => setVendedor({ id: 0, label: '' })}
                  >
                    <i className="bi bi-x"></i>
                  </span>
                </div>
              )}
            </div>
          )}
          {categorias.length > 0 && (
            <div className={styles.filtroItem}>
              <h3>Categoria:</h3>
              {categorias.slice(0, MAX_OPCIONES_VISIBLES).map((cat) => (
                <span
                  key={cat.value}
                  className={styles.crumb}
                  onClick={() => onChangeCategoria(cat.value, cat.label)}
                >
                  {cat.label}
                </span>
              ))}
              {categorias.length > MAX_OPCIONES_VISIBLES && (
                <span className={styles.verMas} onClick={() => verTodas(categorias, 'categoria')}>
                  Ver Todas
                </span>
              )}
            </div>
          )}
          {filtrosFijos.map(
            ({ titulo, value, opciones, onChange }) =>
              opciones.length !== 0 && (
                <div key={titulo} className={styles.filtroItem}>
                  <h3>{titulo}:</h3>
                  {ordenarOpciones(opciones, value)
                    .slice(0, MAX_OPCIONES_VISIBLES)
                    .map(({ label, valor }) => (
                      <RadioInput
                        key={valor}
                        valor={label}
                        isSelected={String(value) === String(valor)}
                        onClick={(l) => {
                          if (l === label) onChange(valor)
                          else onChange('')
                        }}
                      />
                    ))}
                  {opciones.length > MAX_OPCIONES_VISIBLES && (
                    <span
                      className={styles.verMas}
                      onClick={() => verTodas(opciones, 'filtroFijo', titulo)}
                    >
                      Ver Todas
                    </span>
                  )}
                </div>
              )
          )}
          {mostrarPrecio && (
            <div className={styles.filtroItem}>
              <h3>Precio:</h3>
              <div className={styles.precioInputs}>
                <input
                  type="number"
                  placeholder="Desde"
                  value={filtrosLocales.precioDesde ?? ''}
                  onChange={(e) => handlePrecioChange(e.target.value, 'desde')}
                  onKeyDown={handleKeyDownPrecio}
                  className={styles.precioInput}
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Hasta"
                  value={filtrosLocales.precioHasta ?? ''}
                  onChange={(e) => handlePrecioChange(e.target.value, 'hasta')}
                  onKeyDown={handleKeyDownPrecio}
                  className={styles.precioInput}
                  min="0"
                />
              </div>
            </div>
          )}
          {Object.entries(filtrosCaracteristicas).map(([etiqueta, valores]) => {
            const selectedValue = filtrosSeleccionados[etiqueta]
            const valoresOrdenados = selectedValue
              ? [...valores].sort((a, b) => {
                  if (a === selectedValue) return -1
                  if (b === selectedValue) return 1
                  return 0
                })
              : valores
            return (
              <div className={styles.filtroItem} key={etiqueta}>
                <h3>{etiqueta}:</h3>
                {valoresOrdenados.slice(0, MAX_OPCIONES_VISIBLES).map((valor) => {
                  const isSelected = filtrosSeleccionados[etiqueta] === valor
                  return (
                    <RadioInput
                      key={valor}
                      onClick={(v) => {
                        dispatch(
                          setFiltrosSeleccionados({ ...filtrosSeleccionados, [etiqueta]: v })
                        )
                      }}
                      valor={valor}
                      isSelected={isSelected}
                    />
                  )
                })}
                {valores.length > MAX_OPCIONES_VISIBLES && (
                  <span
                    className={styles.verMas}
                    onClick={() =>
                      verTodas(
                        valores.map((v) => ({ value: v, label: v })),
                        'caracteristica',
                        etiqueta
                      )
                    }
                  >
                    Ver Todas
                  </span>
                )}
              </div>
            )
          })}
          {entradasFecha.length > 0 && (
            <div className={styles.filtroItem}>
              {entradasFecha.map((entrada) => (
                <FechaSelect
                  key={entrada.key}
                  fecha={filtrosLocales[entrada.key] ?? null}
                  guardarFecha={handleFechaChange(
                    entrada.key,
                    entrada.key.includes('Desde')
                      ? entrada.key.replace('Desde', 'Hasta')
                      : entrada.key.replace('Hasta', 'Desde')
                  )}
                  label={entrada.label}
                  isSelected={Boolean(filtrosLocales[entrada.key])}
                  deshabilitaFechaFutura={new Date().toISOString().split('T')[0]}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

Filtros.propTypes = {
  mapaFiltros: PropTypes.object,
  contextoFiltros: PropTypes.object,
  arrayAFiltrar: PropTypes.arrayOf(PropTypes.object),
  filtrosVisibles: PropTypes.arrayOf(PropTypes.string),
  buscador: PropTypes.shape({
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    valor: PropTypes.string,
    setBusqueda: PropTypes.func
  }),
  limpiarFiltros: PropTypes.func
}
